import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Leaf, ExternalLink, Beaker, ArrowRight, Sprout, Mountain, MapPin, ShieldCheck, Salad, Package, HeartHandshake, Wheat } from "lucide-react";
import ProductPlaceholder from "@/components/ProductPlaceholder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Uploaded/generated images are stored as relative paths (/api/library/image/<id>) — absolutize them.
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

// Sourcing / provenance badges — surfacing Badi's environmental & Indigenous ethos.
const PROVENANCE_BADGES = {
  "regeneratively-farmed": { label: "Regeneratively Farmed", Icon: Sprout },
  "indigenous-grown": { label: "Indigenous-Grown", Icon: Mountain },
  "wildharvested": { label: "Wildharvested on Country", Icon: Leaf },
  "australian-made": { label: "Australian-Made", Icon: MapPin },
  "palm-free": { label: "Palm-Free", Icon: ShieldCheck },
  "vegan": { label: "Vegan", Icon: Salad },
  "small-batch": { label: "Small Batch", Icon: Package },
  "community-supporting": { label: "Supports Indigenous Community", Icon: HeartHandshake },
};

function ProvenanceBadges({ productId, provenance }) {
  if (!provenance || provenance.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-5" data-testid={`provenance-${productId}`}>
      {provenance.map((key) => {
        const badge = PROVENANCE_BADGES[key];
        if (!badge) return null;
        const { label, Icon } = badge;
        return (
          <span
            key={key}
            data-testid={`provenance-${productId}-${key}`}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[1.5px] text-[#3F6B4C] bg-[#EAEFE4] border border-[#CBD9BE] rounded-full px-2.5 py-1"
          >
            <Icon className="w-3 h-3" strokeWidth={1.75} /> {label}
          </span>
        );
      })}
    </div>
  );
}

function ProductCard({ product, memberId }) {
  // Outbound link goes through our redirector so we can attribute the click.
  const redirectUrl = `${BACKEND_URL}/api/r/${product.id}${memberId ? `?m=${encodeURIComponent(memberId)}` : ""}`;
  const fallback = !product.image_url;
  return (
    <article
      data-testid={`apothecary-card-${product.id}`}
      className="flex flex-col bg-white border border-[#E8DFD0] hover:border-[#C05C42] transition-colors"
    >
      <a
        href={redirectUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`apothecary-image-link-${product.id}`}
        className="aspect-square bg-[#F1ECE4] flex items-center justify-center overflow-hidden"
      >
        {fallback ? (
          <ProductPlaceholder
            name={product.name}
            category={product.category}
            tags={product.tags}
            productId={product.id}
            subtitle={product.subtitle}
          />
        ) : (
          <img
            src={imgSrc(product.image_url)}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        )}
      </a>
      <div className="p-6 flex flex-col flex-1">
        {product.category && (
          <p className="label-eyebrow mb-2">{product.category}</p>
        )}
        <h3 className="font-serif text-2xl text-[#2D322B] mb-2 leading-tight">{product.name}</h3>
        {product.tagline && (
          <p className="text-sm text-[#5C6359] italic mb-4">{product.tagline}</p>
        )}
        <p className="text-sm text-[#5C6359] leading-relaxed mb-6 flex-1">{product.why_chosen}</p>
        <ProvenanceBadges productId={product.id} provenance={product.provenance} />
        <div className="flex items-baseline justify-between border-t border-[#E8DFD0] pt-4">
          <p className="font-serif text-xl text-[#2D322B]" data-testid={`apothecary-price-${product.id}`}>
            A${Number(product.price_aud).toFixed(2)}
          </p>
          <a
            href={redirectUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`apothecary-buy-${product.id}`}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[2px] text-white bg-[#C05C42] hover:bg-[#A04A33] px-5 py-3 transition-colors"
          >
            Shop on badi.com.au <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </article>
  );
}

function WritingCard({ writing, memberId }) {
  return (
    <article className="flex flex-col bg-white border border-[#E8DFD0] rounded-2xl overflow-hidden" data-testid={`writing-card-${writing.id}`}>
      <Link to={`/library/${writing.id}`} className="block h-44 overflow-hidden bg-[#EAEFE4] group">
        {writing.image ? (
          <img src={imgSrc(writing.image)} alt={writing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Leaf className="w-8 h-8 text-[#3F6B4C]" /></div>
        )}
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-[1.5px] text-[#3F6B4C]">{writing.category}{writing.tradition ? ` · ${writing.tradition}` : ""}</p>
        <Link to={`/library/${writing.id}`} className="mt-2">
          <h3 className="font-serif text-xl text-[#2D322B] leading-snug hover:text-[#C05C42] transition-colors">{writing.title}</h3>
        </Link>
        {writing.excerpt && <p className="text-sm text-[#5C6359] mt-2 leading-relaxed line-clamp-3">{writing.excerpt}</p>}
        <div className="mt-4 pt-4 border-t border-[#E8DFD0] flex flex-col gap-3 mt-auto">
          {writing.products?.length > 0 ? (
            <>
              <p className="text-[10px] uppercase tracking-[1.5px] text-[#9A8F7E]">Shop the medicine</p>
              {writing.products.map((p) => (
                <a key={p.id} href={`${BACKEND_URL}/api/r/${p.id}${memberId ? `?m=${encodeURIComponent(memberId)}` : ""}`}
                  target="_blank" rel="noreferrer" data-testid={`writing-product-${writing.id}-${p.id}`}
                  className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#EAEFE4] shrink-0">
                    {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[#2D322B] truncate group-hover:text-[#C05C42] transition-colors">{p.name}</p>
                    {p.price_aud != null && <p className="text-[11px] text-[#9A8F7E]">${p.price_aud} AUD</p>}
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-[#C05C42] shrink-0" />
                </a>
              ))}
            </>
          ) : (
            <Link to={`/library/${writing.id}`} className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B] inline-flex items-center gap-1.5" data-testid={`writing-read-${writing.id}`}>
              Read the writing <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Apothecary() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [writings, setWritings] = useState([]);
  const [loading, setLoading] = useState(true);
  const memberId = user?.id;

  useEffect(() => {
    api.get("/apothecary")
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
    api.get("/apothecary/writings").then((r) => setWritings(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16">
          <p className="label-eyebrow mb-4">The Apothecary</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D322B] mb-6 leading-tight">
            Plant medicines &amp; rituals, curated.
          </h1>
          <p className="text-base text-[#5C6359] leading-relaxed">
            A small, hand-tended shelf of supplements, native botanicals and skin rituals from the Badi line.
            Every product is selected by Dr Sam and her practitioner team for its evidence, lineage, and quality.
            Checkout happens on our store, <span className="text-[#2D322B]">badi.com.au</span> — shipping worldwide.
          </p>
          <p className="text-xs text-[#9A8F7E] mt-4">
            Prices in AUD. Shipping calculated at checkout on badi.com.au.
          </p>
        </div>

        {/* Custom prescription CTA — our clinical differentiator */}
        <Link
          to="/prescription-request"
          data-testid="apothecary-rx-cta"
          className="block bg-[#2D322B] hover:bg-[#1F231D] transition-colors p-8 sm:p-10 mb-16 group"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-[#C05C42]/15 flex items-center justify-center flex-shrink-0">
                <Beaker className="w-5 h-5 text-[#D49A44]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="label-eyebrow text-[#D49A44] mb-2">Clinician-tailored prescription</p>
                <h3 className="font-serif text-2xl text-[#F9F6F0] mb-2 leading-tight">
                  Need something compounded to your level?
                </h3>
                <p className="text-sm text-[#C2BFB6] leading-relaxed max-w-xl">
                  Our clinic creates prescription-only supplements in a regulated Australian laboratory —
                  precision-dosed for your biomarkers, allergies, and goals. Not off-the-shelf, not generic.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[#F9F6F0] text-[11px] uppercase tracking-[2px] flex-shrink-0">
              Request a formulation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>

        {loading ? (
          <p data-testid="apothecary-loading" className="text-[#9A8F7E]">Loading apothecary…</p>
        ) : products.length === 0 ? (
          <p data-testid="apothecary-empty" className="text-[#9A8F7E]">No products available right now.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="apothecary-grid">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} memberId={user?.id} />
            ))}
          </div>
        )}

        {/* Plant Medicine writings ↔ products */}
        {writings.length > 0 ? (
          <div className="mt-16" data-testid="plant-medicine-library">
            <div className="flex items-start gap-5 mb-8">
              <div className="w-12 h-12 rounded-full bg-[#3F6B4C]/12 flex items-center justify-center flex-shrink-0">
                <Wheat className="w-5 h-5 text-[#3F6B4C]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="label-eyebrow text-[#3F6B4C] mb-2">Plant Medicine Library</p>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#2D322B] mb-2 leading-tight">
                  The plants, the science & where to find them.
                </h3>
                <p className="text-sm text-[#5C6359] leading-relaxed max-w-2xl">
                  Writings on our plant and herbal medicines — traditional knowledge alongside modern research —
                  each linked to the products they describe. Native foods as medicine (Kakadu plum, wattleseed,
                  gumbi gumbi and more) join the shelf as they're sourced on Country. Educational only, always alongside your practitioner.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plant-medicine-writings">
              {writings.map((w) => <WritingCard key={w.id} writing={w} memberId={memberId} />)}
            </div>
          </div>
        ) : (
          <div
            data-testid="nutritional-medicine-coming-soon"
            className="mt-16 border border-dashed border-[#CBD9BE] bg-[#EAEFE4]/50 p-8 sm:p-10"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-[#3F6B4C]/12 flex items-center justify-center flex-shrink-0">
                  <Wheat className="w-5 h-5 text-[#3F6B4C]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="label-eyebrow text-[#3F6B4C] mb-2">Nutritional Medicine</p>
                  <h3 className="font-serif text-2xl text-[#2D322B] mb-2 leading-tight">
                    Native foods as medicine — coming soon.
                  </h3>
                  <p className="text-sm text-[#5C6359] leading-relaxed max-w-xl">
                    A new shelf of nutrient-dense bushfoods and regeneratively-grown wholefoods —
                    Kakadu plum, wattleseed, native superfoods — sourced on Country and chosen for their
                    documented longevity and metabolic benefits. Grown with care for soil, community and Country.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[2px] text-[#3F6B4C] border border-[#CBD9BE] rounded-full px-4 py-2 flex-shrink-0 self-start sm:self-center whitespace-nowrap">
                <Sprout className="w-3.5 h-3.5" /> In cultivation
              </span>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
